import { MatTooltip, MatTooltipDefaultOptions } from '@angular/material/tooltip';

/**
 * The control vocabulary's tooltip timing (canon §3m; NBO-2420): 400ms on hover, INSTANT on
 * keyboard focus. App-wide — every matTooltip.
 *
 * The hover half is plain configuration (`TOOLTIP_DEFAULT_OPTIONS`, provided in
 * angular-material.module.ts). The keyboard half is not: Material shows a tooltip on keyboard focus
 * through the same `showDelay` as on hover, read once at construction, with no option to tell the
 * two apart. Its three call sites do differ, though, and that is what this relies on (verified on
 * @angular/material 21.2): the FocusMonitor keyboard branch calls `show()` with NO arguments,
 * while mouseenter and the touch long-press call `show(undefined, origin)`. So a call with no
 * arguments is keyboard focus (or an explicit programmatic show — none exist in this app) and gets
 * delay 0; every other call is untouched.
 *
 * A directive on `[matTooltip]` would only reach templates that import it — 44 places import
 * MatTooltipModule directly — so this wraps the method once, at boot. If a Material upgrade changes
 * the call shape the wrapper simply stops matching and tooltips fall back to the hover delay on
 * focus: degraded, never broken. Re-check `_tooltip-chunk.mjs` (`origin === 'keyboard'`) on upgrade.
 */
export const TOOLTIP_DEFAULT_OPTIONS: MatTooltipDefaultOptions = {
  showDelay: 400,
  hideDelay: 0,
  touchendHideDelay: 1500
};

const INSTALLED = Symbol('nboInstantKeyboardTooltip');

export function installInstantKeyboardTooltips(): void {
  const proto = MatTooltip.prototype as MatTooltip & { [INSTALLED]?: true };
  if (proto[INSTALLED] || typeof proto.show !== 'function') return;
  const show = proto.show;
  proto.show = function (this: MatTooltip, ...args: Parameters<MatTooltip['show']>) {
    return args.length === 0 ? show.call(this, 0) : show.apply(this, args);
  };
  proto[INSTALLED] = true;
}
